
import React, { useState, useEffect, useCallback, useMemo } from "react";
import SearchBar from "../components/CommonComponents/SearchInput";
import PrimaryButton from "../components/CommonComponents/PrimaryButton";
import { useTheme } from "../components/CommonComponents/ThemeProvider";
import EmptyState from "../components/CommonComponents/EmptyState";
import TableLoadingSkeleton from "../components/CommonComponents/TableLoadingSkeleton";
// import ViewToggle from "../components/CommonComponents/ViewToggle";
import axios from "axios";
import DeleteModal from "../components/CommonComponents/ConfirmationModal";
import AddUserModal from "../components/admin/AddUserModal";
import Pagination from "../components/CommonComponents/Pagination";
import { showSuccessToast, showErrorToast } from "../utils/toast";
import UserTable from '../components/admin/users/UserTable';
import { Users } from 'lucide-react';

const ROLES_TO_EXCLUDE = ["Admin", "SuperAdmin"];

export default function UserListPage() {
  useTheme();
  const [refreshFlag, setRefreshFlag] = useState(false);
  // ===================
  // State
  // ===================
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalState, setModalState] = useState({
    type: null,
    user: null,
    open: false,
  });

  const usersPerPage = 5;

  // ===================
  // Current logged-in user
  // ===================
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  }, []);

  const baseURL = `${process.env.REACT_APP_API_URL}/api/admin/users`;

  // ===================
  // Load users
  // ===================
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const res = await axios.get(baseURL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data.data || []);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers, refreshFlag]);

  // ===================
  // Filter & paginate
  // ===================
  const filteredUsers = users.filter((user) => {
    const matchesCustomer = user.customer_id === currentUser.customer_id;
    const roleExcluded = ROLES_TO_EXCLUDE.includes(user.role);
    const matchesSearch = user.username
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchesCustomer && !roleExcluded && matchesSearch;
  });

  const totalPages = Math.max(Math.ceil(filteredUsers.length / usersPerPage), 1);
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [filteredUsers, currentPage, totalPages]);

  // ===================
  // Modal handlers
  // ===================
  const closeModal = () =>
    setModalState({ type: null, user: null, open: false });
  const handleAddUser = () =>
    setModalState({ type: "add", user: null, open: true });
  const handleEditUser = (user) =>
    setModalState({ type: "edit", user, open: true });

  const handleUserAddedOrUpdated = () => {
    loadUsers();
    closeModal();
    setRefreshFlag(prev => !prev);
  };

  const handleConfirmDelete = async () => {
    if (!modalState.user) return;
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      await axios.put(
        `${baseURL}/${modalState.user.id}/status`,
        { status: "DELETED" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers((prev) =>
        prev.map((u) =>
          u.id === modalState.user.id ? { ...u, status: "DELETED" } : u
        )
      );
      closeModal();
      showSuccessToast("User deleted successfully.");
    } catch (error) {
      console.error("Error deleting user:", error);
      showErrorToast("Failed to delete user.");
    }
  };

  const handleToggleUserStatus = async () => {
    if (!modalState.user) return;
    try {
      const newStatus =
        modalState.user.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      await axios.put(
        `${baseURL}/${modalState.user.id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers((prev) =>
        prev.map((u) =>
          u.id === modalState.user.id ? { ...u, status: newStatus } : u
        )
      );
      closeModal();
      showSuccessToast(`User status set to ${newStatus}.`);
    } catch (error) {
      console.error("Error toggling status:", error);
      showErrorToast("Failed to update user status.");
    }
  };

  // ===================
  // JSX
  // ===================
  return (
    <div className="pt-6 flex flex-col gap-6">
      {error && (
        <EmptyState
          icon={Users}
          title="Failed to Load Users"
          message="Check your internet connection and try again."
          onRetry={loadUsers}
          retryLabel="Retry"
        />
      )}
      {!error && (
        <>
          {/* Search & Add */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <SearchBar
              placeholder="Search by username..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              disabled={loading}
            />
            <PrimaryButton onClick={handleAddUser} disabled={loading}>+ Add User</PrimaryButton>
          </div>

          {/* Header + View Toggle */}
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--color-text)' }}>
              <Users className="w-6 h-6" />
              Users list
            </h2>

          </div>

          <div className="flex flex-col gap-2">
            {/* Users Table */}
            {loading ? (
              <div className="rounded-lg border border-gray-200 bg-white overflow-auto max-h-[calc(100vh-340px)] min-h-[300px]">
                <TableLoadingSkeleton
                  columnLabels={[
                    "No.",
                    "Username",
                    "Email",
                    "Role",
                    "Branch",
                    "Status",
                    "Actions",
                  ]}
                  columnTypes={["text", "text", "text", "text", "text", "badge", "actions"]}
                  columnTemplate="0.4fr 1.2fr 1.8fr 0.9fr 1.1fr 0.9fr 0.9fr"
                  rowCount={4}
                  headerCellClassName="text-left"
                />
              </div>
            ) : (
              <UserTable
                users={currentUsers}
                onEdit={handleEditUser}
                onDelete={user => setModalState({ type: "delete", user, open: true })}
                onToggleStatus={user => setModalState({ type: "toggle", user, open: true })}
              />
            )}

            {/* Pagination */}
            <div className="flex flex-col items-center gap-2">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => setCurrentPage(page)}
              />
              <p className="text-sm" style={{ color: 'var(--color-text)', opacity: 0.7 }}>
                Showing {filteredUsers.length === 0 ? 0 : indexOfFirstUser + 1}
                – {Math.min(indexOfLastUser, filteredUsers.length)}{' '}
                of {filteredUsers.length} users | Page {currentPage} of {totalPages}
              </p>
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      {modalState.open && (modalState.type === "add" || modalState.type === "edit") && (
        <AddUserModal
          editData={modalState.type === "edit" ? modalState.user : null}
          onClose={closeModal}
          onUserAddedOrUpdated={handleUserAddedOrUpdated}
        />
      )}
      {modalState.open && modalState.type === "delete" && (
        <DeleteModal
          isOpen
          onClose={closeModal}
          onConfirm={handleConfirmDelete}
          title="Delete User"
          message={`Are you sure you want to delete "${modalState.user?.username}"?`}
          confirmText="Yes"
          cancelText="No"
          type="delete"
        />
      )}
      {modalState.open && modalState.type === "toggle" && (
        <DeleteModal
          isOpen
          onClose={closeModal}
          onConfirm={handleToggleUserStatus}
          title="Change User Status"
          message={`Are you sure you want to set this user to ${modalState.user?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE"}?`}
          confirmText="Yes"
          cancelText="No"
          type="delete"
        />
      )}
    </div>
  );
}
